import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";

import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

const emptyProductData = {
  name: "",
  SKU: "",
  cardSummary: "",
  cardCallout: "",
  shortDescription: "",
  longDescription: "",
  bulletPoint1: "",
  bulletPoint2: "",
  bulletPoint3: "",
  bulletPoint4: "",
  bulletPoint5: "",
  msrp: "",
  price: "",
  images: [],
  imageURIs: [],
  audioURIs: [],
  controls: [],
  UPC: "",
  productURI: "",
  productVersion: "",
  windowsDownloadURI: "",
  macDownloadURI: "",
  versionDownloads: [],
  googleMarketplaceImage: "",
  visibleOnWebsite: true,
  starRating: "",
  ratingCount: "",
};

const cloneProductForCreate = (product) => {
  if (!product) return emptyProductData;

  return {
    name: `${product.name || ""} Copy`,
    SKU: "",
    cardSummary: product.cardSummary || "",
    cardCallout: product.cardCallout || "",
    shortDescription: product.shortDescription || "",
    longDescription: product.longDescription || "",
    bulletPoint1: product.bulletPoint1 || "",
    bulletPoint2: product.bulletPoint2 || "",
    bulletPoint3: product.bulletPoint3 || "",
    bulletPoint4: product.bulletPoint4 || "",
    bulletPoint5: product.bulletPoint5 || "",
    msrp: product.msrp ?? "",
    price: product.price ?? "",
    images: Array.isArray(product.images) ? product.images : [],
    imageURIs: Array.isArray(product.imageURIs) ? product.imageURIs : [],
    audioURIs: Array.isArray(product.audioURIs) ? product.audioURIs : [],
    controls: Array.isArray(product.controls) ? product.controls : [],
    UPC: "",
    productURI: "",
    productVersion: product.productVersion || "",
    windowsDownloadURI: product.windowsDownloadURI || "",
    macDownloadURI: product.macDownloadURI || "",
    versionDownloads: Array.isArray(product.versionDownloads)
      ? product.versionDownloads
      : [],
    googleMarketplaceImage: product.googleMarketplaceImage || "",
    visibleOnWebsite: Boolean(product.visibleOnWebsite),
    starRating: product.starRating ?? "",
    ratingCount: product.ratingCount ?? "",
  };
};

export default function AdminProductsPage() {
  const { style } = useStyle();
  const navigate = useNavigate();

  const token = useMemo(() => localStorage.getItem("token") || "", []);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copyFromProductId, setCopyFromProductId] = useState("");
  const [newProductData, setNewProductData] = useState(emptyProductData);

  const panelBg = style.darkPanel || "rgba(9, 12, 18, 0.45)";

  const sharedTextFieldSx = {
    width: "100%",
    "& .MuiInputBase-root": {
      color: style.color4,
      backgroundColor: style.color2,
      borderRadius: "8px",
    },
    "& .MuiOutlinedInput-root fieldset": {
      borderColor: style.color2,
    },
    "& .MuiOutlinedInput-root:hover fieldset": {
      borderColor: style.color3,
    },
    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
      borderColor: style.accentGreen,
    },
    "& .MuiInputLabel-root": {
      color: style.color3,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: style.color4,
    },
    "& .MuiFormHelperText-root": {
      color: style.color3,
      marginLeft: 0,
    },
  };

  const tableCellSx = {
    color: style.color4,
    borderColor: "rgba(255,255,255,0.08)",
    whiteSpace: "nowrap",
  };

  const tableHeadCellSx = {
    ...tableCellSx,
    color: style.color3,
    fontWeight: 800,
    backgroundColor: "rgba(255,255,255,0.04)",
  };

  const sectionCardSx = {
    p: { xs: 1.5, sm: 2 },
    borderRadius: "14px",
    backgroundColor: panelBg,
    border: "1px solid rgba(255,255,255,0.08)",
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post("/api/get-all-products");

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      setProducts(Array.isArray(data) ? data : data.products || []);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Unable to load products.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    loadProducts();
  }, []);

  const openCreateModal = () => {
    setCopyFromProductId("");
    setNewProductData(emptyProductData);
    setCreateOpen(true);
  };

  const closeCreateModal = () => {
    setCreateOpen(false);
    setCopyFromProductId("");
    setNewProductData(emptyProductData);
  };

  const handleCopyFromChange = (productId) => {
    setCopyFromProductId(productId);

    if (!productId) {
      setNewProductData(emptyProductData);
      return;
    }

    const productToCopy = products.find((product) => product._id === productId);
    setNewProductData(cloneProductForCreate(productToCopy));
  };

  const handleNewProductChange = (field, value) => {
    setNewProductData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const safeNumber = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const number = Number(value);
    return Number.isNaN(number) ? "" : number;
  };

  const buildCreatePayload = () => ({
    ...newProductData,
    msrp: safeNumber(newProductData.msrp),
    price: safeNumber(newProductData.price),
    starRating: safeNumber(newProductData.starRating),
    ratingCount: safeNumber(newProductData.ratingCount),
  });

  const createProduct = async () => {
    try {
      if (!token) {
        toast.error("You must be logged in as an admin.");
        return;
      }

      setCreating(true);

      const productData = buildCreatePayload();

      const { data } = await axios.post("/api/create-product", {
        token,
        productData,
      });

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      toast.success("Product created.");
      closeCreateModal();
      await loadProducts();

      if (data?._id) {
        navigate(`/admin/products/${data._id}`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || "Unable to create product.");
    } finally {
      setCreating(false);
    }
  };

  const formatCurrency = (value) => {
    const number = Number(value || 0);
    return `$${number.toFixed(2)}`;
  };

  return (
    <>
      <Seo
        title="Admin Products | Parish Audio"
        description="Manage Parish Audio products."
        canonical="https://parishaudio.com/admin/products"
        robots="noindex,nofollow"
      />

      <Box
        sx={{
          backgroundColor: "black",
          minHeight: "100vh",
          color: style.color4,
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 2, md: 3 },
        }}
      >
        <Box sx={{ maxWidth: 1350, mx: "auto" }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "20px",
              overflow: "hidden",
              backgroundColor: style.color1,
              border: `1px solid ${style.color2}`,
            }}
          >
            <Box
              sx={{
                px: { xs: 1.5, sm: 2, md: 3 },
                py: { xs: 2.25, md: 3 },
                backgroundColor: panelBg,
                borderBottom: `1px solid ${style.color2}`,
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
              >
                <Stack spacing={1} alignItems="flex-start">
                    <a href="/admin">
                  <Chip
                    icon={<Inventory2RoundedIcon />}
                    label="Admin"
                    sx={{
                      borderRadius: "5px",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: "rgba(255,255,255,0.06)",
                    }}
                  />
                    </a>
                  <Typography
                    sx={{
                      color: style.color4,
                      fontSize: { xs: "1.9rem", md: "2.5rem" },
                      fontWeight: 800,
                      lineHeight: 1.05,
                      letterSpacing: "-0.03em",
                    }}
                  >
                    Products
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.65 }}>
                    View all products, open a product to edit details, or create
                    a new product from scratch or copied from an existing item.
                  </Typography>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    startIcon={<RefreshRoundedIcon />}
                    onClick={loadProducts}
                    sx={{
                      borderRadius: "5px",
                      textTransform: "none",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    Refresh
                  </Button>

                  <Button
                    startIcon={<AddRoundedIcon />}
                    onClick={openCreateModal}
                    sx={{
                      borderRadius: "5px",
                      textTransform: "none",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: style.accentGreen,
                    }}
                  >
                    New Product
                  </Button>
                </Stack>
              </Stack>
            </Box>

            <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, py: 2.5 }}>
              <Paper elevation={0} sx={sectionCardSx}>
                <Stack spacing={1.5}>
                  <Typography
                    sx={{
                      color: style.color4,
                      fontWeight: 800,
                      fontSize: "1.05rem",
                    }}
                  >
                    Existing Products
                  </Typography>

                  {loading ? (
                    <Typography sx={{ color: style.color3 }}>
                      Loading products...
                    </Typography>
                  ) : products.length === 0 ? (
                    <Typography sx={{ color: style.color3 }}>
                      No products found.
                    </Typography>
                  ) : (
                    <TableContainer
                      sx={{
                        overflowX: "auto",
                        borderRadius: "10px",
                        border: "1px solid rgba(255,255,255,0.08)",
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={tableHeadCellSx}>Name</TableCell>
                            <TableCell sx={tableHeadCellSx}>SKU</TableCell>
                            <TableCell sx={tableHeadCellSx}>Version</TableCell>
                            <TableCell sx={tableHeadCellSx}>URI</TableCell>
                            <TableCell sx={tableHeadCellSx}>Price</TableCell>
                            <TableCell sx={tableHeadCellSx}>Visible</TableCell>
                            <TableCell sx={tableHeadCellSx}>Actions</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {products.map((product) => (
                            <TableRow key={product._id}>
                              <TableCell sx={tableCellSx}>
                                <Typography
                                  sx={{
                                    color: style.color4,
                                    fontWeight: 800,
                                    maxWidth: 260,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {product.name || "Untitled"}
                                </Typography>
                              </TableCell>

                              <TableCell sx={tableCellSx}>
                                {product.SKU || "—"}
                              </TableCell>

                              <TableCell sx={tableCellSx}>
                                {product.productVersion || "—"}
                              </TableCell>

                              <TableCell sx={tableCellSx}>
                                {product.productURI || "—"}
                              </TableCell>

                              <TableCell sx={tableCellSx}>
                                {formatCurrency(product.price)}
                              </TableCell>

                              <TableCell sx={tableCellSx}>
                                {product.visibleOnWebsite ? "Yes" : "No"}
                              </TableCell>

                              <TableCell sx={tableCellSx}>
                                <Button
                                  size="small"
                                  startIcon={<EditRoundedIcon />}
                                  onClick={() =>
                                    navigate(`/admin/products/${product._id}`)
                                  }
                                  sx={{
                                    borderRadius: "5px",
                                    textTransform: "none",
                                    fontWeight: 700,
                                    color: style.color4,
                                    backgroundColor: "rgba(255,255,255,0.05)",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                  }}
                                >
                                  Open
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Stack>
              </Paper>
            </Box>
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={createOpen}
        onClose={closeCreateModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            backgroundColor: style.color1,
            color: style.color4,
            borderRadius: "16px",
            border: `1px solid ${style.color2}`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Create Product</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Copy From Existing Product"
              value={copyFromProductId}
              onChange={(e) => handleCopyFromChange(e.target.value)}
              select
              size="small"
              sx={sharedTextFieldSx}
              helperText="Optional. Select a product to pre-populate the new product form."
            >
              <MenuItem value="">Start blank</MenuItem>
              {products.map((product) => (
                <MenuItem key={product._id} value={product._id}>
                  {product.name} {product.SKU ? `(${product.SKU})` : ""}
                </MenuItem>
              ))}
            </TextField>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Name"
                  value={newProductData.name}
                  onChange={(e) =>
                    handleNewProductChange("name", e.target.value)
                  }
                  size="small"
                  sx={sharedTextFieldSx}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  label="SKU"
                  value={newProductData.SKU}
                  onChange={(e) => handleNewProductChange("SKU", e.target.value)}
                  size="small"
                  sx={sharedTextFieldSx}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  label="Product Version"
                  value={newProductData.productVersion}
                  onChange={(e) =>
                    handleNewProductChange("productVersion", e.target.value)
                  }
                  size="small"
                  sx={sharedTextFieldSx}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Product URI"
                  value={newProductData.productURI}
                  onChange={(e) =>
                    handleNewProductChange("productURI", e.target.value)
                  }
                  size="small"
                  sx={sharedTextFieldSx}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="MSRP"
                  type="number"
                  value={newProductData.msrp}
                  onChange={(e) =>
                    handleNewProductChange("msrp", e.target.value)
                  }
                  size="small"
                  sx={sharedTextFieldSx}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Price"
                  type="number"
                  value={newProductData.price}
                  onChange={(e) =>
                    handleNewProductChange("price", e.target.value)
                  }
                  size="small"
                  sx={sharedTextFieldSx}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Card Summary"
                  value={newProductData.cardSummary}
                  onChange={(e) =>
                    handleNewProductChange("cardSummary", e.target.value)
                  }
                  multiline
                  minRows={2}
                  size="small"
                  sx={sharedTextFieldSx}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Short Description"
                  value={newProductData.shortDescription}
                  onChange={(e) =>
                    handleNewProductChange("shortDescription", e.target.value)
                  }
                  multiline
                  minRows={2}
                  size="small"
                  sx={sharedTextFieldSx}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={closeCreateModal}
            sx={{
              color: style.color4,
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={createProduct}
            disabled={creating}
            sx={{
              borderRadius: "5px",
              textTransform: "none",
              fontWeight: 700,
              color: style.color4,
              backgroundColor: style.accentGreen,
            }}
          >
            {creating ? "Creating..." : "Create Product"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}